import { ref } from "vue";
import axios from "vue-app/utils/axios";

interface BrandingCache {
  footerHtml: string | null;
  productBanner: string | null;
  aiBanners: Map<number, string>;
}

const cache = ref<BrandingCache>({
  footerHtml: null,
  productBanner: null,
  aiBanners: new Map(),
});

const loadingStates = {
  footerHtml: false,
  productBanner: false,
  aiBanners: new Map<number, boolean>(),
};

export function useBrandingCache() {
  const getFooterHtml = async (): Promise<string> => {
    if (cache.value.footerHtml !== null) {
      return cache.value.footerHtml;
    }

    if (loadingStates.footerHtml) {
      await waitForLoading("footerHtml");
      return cache.value.footerHtml || "";
    }

    loadingStates.footerHtml = true;

    try {
      const { data } = await axios.get<string>("/tracker-branding/footer-html", {
        headers: { Accept: "text/html" },
      });
      cache.value.footerHtml = typeof data === "string" ? data : "";
      return cache.value.footerHtml;
    } catch {
      cache.value.footerHtml = "";
      return "";
    } finally {
      loadingStates.footerHtml = false;
    }
  };

  const getProductBanner = async (): Promise<string> => {
    if (cache.value.productBanner !== null) {
      return cache.value.productBanner;
    }

    if (loadingStates.productBanner) {
      await waitForLoading("productBanner");
      return cache.value.productBanner || "";
    }

    loadingStates.productBanner = true;

    try {
      const { data } = await axios.get<string>("/product-banner");
      cache.value.productBanner = typeof data === "string" ? data.trim() : "";
      return cache.value.productBanner;
    } catch {
      cache.value.productBanner = "";
      return "";
    } finally {
      loadingStates.productBanner = false;
    }
  };

  const getAiBanner = async (engagementTypeId: number): Promise<string> => {
    if (cache.value.aiBanners.has(engagementTypeId)) {
      return cache.value.aiBanners.get(engagementTypeId) || "";
    }

    if (loadingStates.aiBanners.get(engagementTypeId)) {
      await waitForLoading("aiBanner", engagementTypeId);
      return cache.value.aiBanners.get(engagementTypeId) || "";
    }

    loadingStates.aiBanners.set(engagementTypeId, true);

    try {
      const { data } = await axios.get<string>(`/ai-product-banner?engagement_type_id=${engagementTypeId}`);
      const bannerUrl = typeof data === "string" ? data.trim() : "";
      cache.value.aiBanners.set(engagementTypeId, bannerUrl);
      return bannerUrl;
    } catch {
      cache.value.aiBanners.set(engagementTypeId, "");
      return "";
    } finally {
      loadingStates.aiBanners.set(engagementTypeId, false);
    }
  };

  const waitForLoading = async (type: "footerHtml" | "productBanner" | "aiBanner", engagementTypeId?: number): Promise<void> => {
    const maxWaitTime = 5000;
    const checkInterval = 50;
    let waited = 0;

    return new Promise((resolve) => {
      const interval = setInterval(() => {
        waited += checkInterval;

        const isLoading =
          type === "aiBanner"
            ? loadingStates.aiBanners.get(engagementTypeId!)
            : loadingStates[type];

        if (!isLoading || waited >= maxWaitTime) {
          clearInterval(interval);
          resolve();
        }
      }, checkInterval);
    });
  };

  const clearCache = () => {
    cache.value = {
      footerHtml: null,
      productBanner: null,
      aiBanners: new Map(),
    };
  };

  return {
    getFooterHtml,
    getProductBanner,
    getAiBanner,
    clearCache,
  };
}
